import { __rest, __decorate, __metadata } from 'tslib';
import { Injectable, ɵɵdefineInjectable, ɵɵinject, NgModule } from '@angular/core';
import { HttpParams, HttpHeaders, HttpClient } from '@angular/common/http';
import { NGXLogger, LoggerModule } from 'ngx-logger';
import urlJoin from 'proper-url-join';

let BaseAPIService = class BaseAPIService {
    constructor(http) {
        this.http = http;
    }
    set(baseUri) {
        this.baseUri = baseUri;
    }
    get(params, options) {
        const getHttpOptions = this.buildHttpOptions(Object.assign(Object.assign({}, options), params));
        // Added additional parameter to avoid caching
        // to force IE to refresh the call for instance.
        // const uniqueUrl = this.baseUri + `?tsp=${new Date()}`;
        const uniqueUrl = this.baseUri;
        return this.http.get(uniqueUrl, getHttpOptions);
    }
    post(body, options) {
        const postHttpOptions = this.buildHttpOptions(options);
        return this.http.post(this.baseUri, body, postHttpOptions);
    }
    postMultipart(body, options) {
        const postHttpOptions = this.buildHttpOptions(Object.assign(Object.assign({}, options), { headers: {
                'Content-Type': 'multipart/form-data',
            } }));
        return this.http.post(this.baseUri, body, postHttpOptions);
    }
    put(params, options) {
        const putHttpOptions = this.buildHttpOptions(options);
        return this.http.put(this.baseUri, params, putHttpOptions);
    }
    delete(options) {
        const deleteHttpOptions = this.buildHttpOptions(options);
        return this.http.delete(this.baseUri, deleteHttpOptions);
    }
    deleteMany(ids, options) {
        const deleteManyHttpOptions = this.buildHttpOptions(Object.assign({ body: ids }, options));
        return this.http.delete(this.baseUri, deleteManyHttpOptions);
    }
    // #region "Helpers"
    buildHttpOptions(options = {}) {
        const defaults = { withCredentials: true };
        const { params, body, headers } = options, rest = __rest(options, ["params", "body", "headers"]);
        return Object.assign(Object.assign(Object.assign({}, defaults), rest), { params: new HttpParams(params || {}), body: body || {}, headers: new HttpHeaders(headers || {}) });
    }
};
BaseAPIService.ctorParameters = () => [
    { type: HttpClient }
];
BaseAPIService = __decorate([
    Injectable(),
    __metadata("design:paramtypes", [HttpClient])
], BaseAPIService);

let LoggerService = class LoggerService {
    constructor(ngxLogger) {
        this.ngxLogger = ngxLogger;
    }
    info(msg, source, extraData) {
        this.ngxLogger.info(msg, source, extraData);
    }
    warn(msg, source, extraData) {
        this.ngxLogger.warn(msg, source, extraData);
    }
    trace(msg, source, extraData) {
        this.ngxLogger.trace(msg, source, extraData);
    }
    error(msg, source, extraData) {
        this.ngxLogger.error(msg, source, extraData);
    }
    debug(msg, source, extraData) {
        this.ngxLogger.debug(msg, source, extraData);
    }
    fatal(msg, source, extraData) {
        this.ngxLogger.fatal(msg, source, extraData);
    }
    setLoggerConfig(config) {
        this.ngxLogger.updateConfig(config);
    }
    getLoggerConfig() {
        return this.ngxLogger.getConfigSnapshot();
    }
};
LoggerService.ctorParameters = () => [
    { type: NGXLogger }
];
LoggerService.ɵprov = ɵɵdefineInjectable({ factory: function LoggerService_Factory() { return new LoggerService(ɵɵinject(NGXLogger)); }, token: LoggerService, providedIn: "root" });
LoggerService = __decorate([
    Injectable({
        providedIn: 'root'
    }),
    __metadata("design:paramtypes", [NGXLogger])
], LoggerService);

var Endpoints;
(function (Endpoints) {
    Endpoints["GET_APPROVED_PRODUCTS"] = "approvedProducts";
    Endpoints["SEARCH_APPROVED_PRODUCTS"] = "searchApprovedProducts";
    Endpoints["EXPORT_PRODUCTS"] = "exportApprovedProducts";
    Endpoints["UPDATE_PRODUCTS"] = "updateProducts";
    Endpoints["UPSERT_ASSETS"] = "productAssets";
    Endpoints["SEARCH_PENDING_PRODUCTS"] = "searchPendingProducts";
    Endpoints["APPROVE_PRODUCTS"] = "approve";
    Endpoints["REJECT_PRODUCTS"] = "reject";
    Endpoints["GET_KPI"] = "kpi";
    Endpoints["GET_TERMS_AGGREGATION"] = "termsAggregation";
    Endpoints["COMPARE_PRODUCTS"] = "compare";
})(Endpoints || (Endpoints = {}));

let PimAPIService = class PimAPIService {
    constructor(http) {
        this.http = http;
        this.defaults = { withCredentials: true };
    }
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
    }
    getProductId(productId) {
        return this.get(urlJoin(this.baseUrl, productId));
    }
    putProductId(productId, body) {
        return this.put(urlJoin(this.baseUrl, productId), body);
    }
    postProduct(body) {
        return this.post(this.baseUrl, body);
    }
    deleteProduct() {
        return this.delete(this.baseUrl);
    }
    deleteProducts(ids) {
        return this.delete(this.baseUrl, { body: ids });
    }
    updateProducts(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.UPDATE_PRODUCTS), body);
    }
    searchApprovedProducts(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.SEARCH_APPROVED_PRODUCTS), body);
    }
    exportProducts(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.EXPORT_PRODUCTS), body);
    }
    searchPendingProducts(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.SEARCH_PENDING_PRODUCTS), body);
    }
    getKpi(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.GET_KPI), body);
    }
    getTermsAggregation(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.GET_TERMS_AGGREGATION), body);
    }
    productAssets(importType, supplierKey, body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.UPSERT_ASSETS, importType, supplierKey), body);
    }
    approveProducts(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.APPROVE_PRODUCTS), body);
    }
    rejectProducts(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.REJECT_PRODUCTS), body);
    }
    compareProducts(body) {
        return this.post(urlJoin(this.baseUrl, Endpoints.COMPARE_PRODUCTS), body);
    }
    // http methods
    get(url, options) {
        return this.http.get(url, Object.assign(Object.assign({}, this.defaults), options));
    }
    post(url, body, options) {
        return this.http.post(url, body, Object.assign(Object.assign({}, this.defaults), options));
    }
    put(url, body, options) {
        return this.http.put(url, body, Object.assign(Object.assign({}, this.defaults), options));
    }
    delete(url, options) {
        return this.http.delete(url, Object.assign(Object.assign({}, this.defaults), options));
    }
};
PimAPIService.ctorParameters = () => [
    { type: HttpClient }
];
PimAPIService.ɵprov = ɵɵdefineInjectable({ factory: function PimAPIService_Factory() { return new PimAPIService(ɵɵinject(HttpClient)); }, token: PimAPIService, providedIn: "root" });
PimAPIService = __decorate([
    Injectable({
        providedIn: 'root',
    }),
    __metadata("design:paramtypes", [HttpClient])
], PimAPIService);

let CoreModule = class CoreModule {
};
CoreModule = __decorate([
    NgModule({
        declarations: [],
        imports: [LoggerModule.forRoot(null)],
        providers: [LoggerService, BaseAPIService, PimAPIService],
        exports: [],
    })
], CoreModule);

let LoggerService$1 = class LoggerService {
    constructor(ngxLogger) {
        this.ngxLogger = ngxLogger;
    }
    info(msg, source, extraData) {
        this.ngxLogger.info(msg, source, extraData);
    }
    warn(msg, source, extraData) {
        this.ngxLogger.warn(msg, source, extraData);
    }
    trace(msg, source, extraData) {
        this.ngxLogger.trace(msg, source, extraData);
    }
    error(msg, source, extraData) {
        this.ngxLogger.error(msg, source, extraData);
    }
    debug(msg, source, extraData) {
        this.ngxLogger.debug(msg, source, extraData);
    }
    fatal(msg, source, extraData) {
        this.ngxLogger.fatal(msg, source, extraData);
    }
    setLoggerConfig(config) {
        this.ngxLogger.updateConfig(config);
    }
    getLoggerConfig() {
        return this.ngxLogger.getConfigSnapshot();
    }
};
LoggerService$1.ctorParameters = () => [
    { type: NGXLogger }
];
LoggerService$1.ɵprov = ɵɵdefineInjectable({ factory: function LoggerService_Factory() { return new LoggerService$1(ɵɵinject(NGXLogger)); }, token: LoggerService$1, providedIn: "root" });
LoggerService$1 = __decorate([
    Injectable({
        providedIn: 'root'
    }),
    __metadata("design:paramtypes", [NGXLogger])
], LoggerService$1);

var SessionKeys;
(function (SessionKeys) {
    SessionKeys["user"] = "user";
    SessionKeys["userFullname"] = "userFullname";
    SessionKeys["userID"] = "userID";
    SessionKeys["userAuthID"] = "userAuthID";
    SessionKeys["userSelectionMinDate"] = "userSelectionMinDate";
    SessionKeys["userSelectionMaxDate"] = "userSelectionMaxDate";
    SessionKeys["accountingID"] = "accountingID";
    SessionKeys["accountingAccessToken"] = "accountingAccessToken";
    SessionKeys["accountingTokenEndPointURI"] = "accountingTokenEndPointURI";
    SessionKeys["accountingAuthorized"] = "accountingAuthorized";
    SessionKeys["integratorToken"] = "integratorToken";
})(SessionKeys || (SessionKeys = {}));
let SessionSettingService = class SessionSettingService {
    constructor() {
        this.defaultSettings = {
            displayDateFormat: 'yyyy-MM-dd',
            displayCurrencyFormat: 'n2',
            displayNumberFormat: 'n0',
            defaultPageSize: '100'
        };
        this.sessionKeysEnum = SessionKeys;
        /** empty block */
    }
    getSetting(key) {
        let result = '';
        if ((typeof (Storage) !== 'undefined') && localStorage.getItem(key.toString())) {
            result = localStorage.getItem(key.toString());
        }
        return result;
    }
    createSessionSetting(value) {
        if ((typeof (Storage) !== 'undefined')) {
            localStorage.setItem(SessionKeys.user, value);
        }
    }
    updateSettinKeyValue(key, value) {
        const userSession = this.getSetting(SessionKeys.user);
        if (userSession.length > 0) {
            const session = JSON.parse(userSession);
            if (key === SessionKeys.accountingTokenEndPointURI) {
                session.accountingTokenEndPointURI = value;
            }
            if (key === SessionKeys.accountingAccessToken) {
                session.accountingAccessToken = value;
            }
            if (key === SessionKeys.accountingID) {
                session.accountingID = value;
            }
            if (key === SessionKeys.accountingAuthorized) {
                session.isAccountingAuthorized = value;
            }
            this.createSessionSetting(JSON.stringify(session));
        }
    }
    getSettinKeyValue(key) {
        let value;
        const userSession = this.getSetting(SessionKeys.user);
        if (userSession.length > 0) {
            const session = JSON.parse(userSession);
            if (key === SessionKeys.userID) {
                value = session.userID;
            }
            if (key === SessionKeys.userAuthID) {
                value = session.userAuthID;
            }
            if (key === SessionKeys.accountingTokenEndPointURI) {
                value = session.accountingTokenEndPointURI;
            }
            if (key === SessionKeys.accountingAccessToken) {
                value = session.accountingAccessToken;
            }
            if (key === SessionKeys.accountingID) {
                value = session.accountingID;
            }
            if (key === SessionKeys.accountingAuthorized) {
                value = session.isAccountingAuthorized;
            }
            if (key === SessionKeys.integratorToken) {
                value = session.integratorToken;
            }
            if (key === SessionKeys.userSelectionMinDate) {
                value = session.userSelectionMinDate;
            }
            if (key === SessionKeys.userSelectionMaxDate) {
                value = session.userSelectionMaxDate;
            }
        }
        return value;
    }
    removeSessionSetting() {
        if ((typeof (Storage) !== 'undefined')) {
            localStorage.removeItem(SessionKeys.user);
        }
    }
};
SessionSettingService = __decorate([
    Injectable(),
    __metadata("design:paramtypes", [])
], SessionSettingService);

/*
 * Public API Surface of core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseAPIService, CoreModule, LoggerService$1 as LoggerService, PimAPIService, SessionKeys, SessionSettingService, LoggerService as ɵa };
//# sourceMappingURL=lbmx-phoenix-lib-core.js.map
